import React, { useState } from "react";
import { ChevronDown, Check, CheckCircle2 } from "lucide-react";
import { find, isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import { SplitButton } from "@/components/ui/split-button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator
} from "@/components/ui/command";
import { cn } from "@/utils/utils";
import Text from "@/components/ui/text";
import IconContainer from "@/components/ui/icon-container";
import { Redo } from "@dreamhub/designsystem/icons";
import { OnboardingStage } from "@/pages/settings/components/contractsSettings/types";

interface Props {
  stages: OnboardingStage[];
  currentStageId: string | null;
  onStageSelection: (stageId: string) => void;
  onComplete?: () => void;
  className?: string;
}

const OnboardingStageSelection: React.FC<Props> = ({
  stages,
  currentStageId,
  onStageSelection,
  onComplete,
  className
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const { t } = useTranslation();

  const currentStage = find(stages, (stage) => stage.id === currentStageId);
  const currentIndex = currentStage
    ? stages.findIndex((stage) => stage.id === currentStage.id)
    : 0;
  const nextStage = stages[currentIndex + 1] ?? null;

  const handleMainButtonClick = () => {
    if (!isEmpty(nextStage)) {
      onStageSelection(nextStage!.id);
    }
  };

  return (
    <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <SplitButton
        className={cn("h-8", className)}
        data-test="contract-detailed-view-onboarding-stage-selection"
        mainContent={
          <div className="flex items-center gap-1">
            <IconContainer size="5">
              <Redo />
            </IconContainer>
            <Text variant="label">
              {isEmpty(nextStage)
                ? currentStage?.name
                : `${t("contracts.detailedView.onboarding.proceedTo")} ${nextStage!.name}`}
            </Text>
          </div>
        }
        onMainClick={handleMainButtonClick}
        onSplitClick={() => setIsPopoverOpen(!isPopoverOpen)}
        splitButtonAsChild={true}
        splitContent={
          <PopoverTrigger asChild>
            <ChevronDown
              className={cn(
                "h-5 cursor-pointer text-muted-foreground transition-transform duration-200",
                isPopoverOpen ? "rotate-180" : "rotate-0"
              )}
            />
          </PopoverTrigger>
        }
        isOpen={isPopoverOpen}
      />
      <PopoverContent
        padding={4}
        className="flex flex-col gap-2 w-fit"
        align="end"
        onEscapeKeyDown={() => setIsPopoverOpen(false)}
        sideOffset={4}
      >
        <Command>
          <CommandList>
            <CommandGroup>
              <Text
                variant="smBodyTertiary"
                className="px-2 py-1 block"
              >
                {t("contracts.detailedView.onboarding.onboardingStagePickerLabel")}
              </Text>
              {stages.map((stage) => {
                const isCurrent = stage.id === currentStageId;
                return (
                  <CommandItem
                    key={stage.id}
                    onSelect={() => {
                      if (isCurrent) return;
                      setIsPopoverOpen(false);
                      onStageSelection(stage.id);
                    }}
                    className={cn(
                      "w-full cursor-pointer py-2 pl-2 pr-3 min-h-9 flex justify-between mr-3 rounded-lg transition-all duration-200 ease-in-out",
                      isCurrent
                        ? "font-semibold cursor-not-allowed !bg-transparent hover:!bg-transparent"
                        : "hover:!bg-surface-hover-soft active:!bg-surface-pressed"
                    )}
                  >
                    <Text variant={isCurrent ? "label" : "body"}>
                      {stage.name}
                    </Text>
                    {isCurrent ? (
                      <IconContainer size="5">
                        <Check />
                      </IconContainer>
                    ) : null}
                  </CommandItem>
                );
              })}
              {onComplete && (
                <>
                  <CommandSeparator />
                  <CommandItem
                    onSelect={() => {
                      setIsPopoverOpen(false);
                      onComplete();
                    }}
                    className="w-full cursor-pointer py-2 pl-2 pr-3 min-h-9 flex items-center gap-2 rounded-lg hover:!bg-surface-hover-soft active:!bg-surface-pressed"
                  >
                    <IconContainer size="5" color="secondary">
                      <CheckCircle2 />
                    </IconContainer>
                    <Text variant="body">
                      {t("contracts.detailedView.onboarding.completeOnboarding")}
                    </Text>
                  </CommandItem>
                </>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default OnboardingStageSelection;
